/**
 * ChartCanvas — the shared frame for the telemetry detail charts. It owns the
 * responsive width (ParentSize), inner margins, and the hover interaction
 * (pointer → nearest point index + a crosshair + a floating tooltip). Each chart
 * renders its own series through the children render-prop and supplies tooltip
 * content for the hovered index; ChartCanvas draws the crosshair and positions
 * the tooltip. Interaction-only DOM measurement (localPoint) — no diagram
 * geometry is decided here.
 */
import { useState, type ReactNode } from "react";
import { ParentSize } from "@visx/responsive";
import { localPoint } from "@visx/event";
import { Group } from "@visx/group";
import { CHART_MARGIN, CHART_HEIGHT, CHART_COLORS, indexAtX, xAt } from "./tokens.js";

export interface ChartCanvasChildCtx {
  /** Inner plot width (px) after margins. */
  readonly innerW: number;
  /** Inner plot height (px) after margins. */
  readonly innerH: number;
  /** X pixel (relative to the inner plot) for point `i`. */
  readonly x: (i: number) => number;
  /** Currently hovered point index, or null. */
  readonly hover: number | null;
}

export interface ChartCanvasProps {
  /** Number of points in the series (drives spacing + hover mapping). */
  readonly count: number;
  readonly height?: number;
  readonly ariaLabel?: string;
  /** Render the series inside the (already margin-offset) plot group. */
  readonly children: (ctx: ChartCanvasChildCtx) => ReactNode;
  /** Tooltip body for the hovered index; return null to suppress. */
  readonly tooltip?: (i: number) => ReactNode;
}

export function ChartCanvas({
  count,
  height = CHART_HEIGHT,
  ariaLabel,
  children,
  tooltip,
}: ChartCanvasProps): ReactNode {
  const [hover, setHover] = useState<number | null>(null);
  return (
    <ParentSize className="w-full" debounceTime={0} parentSizeStyles={{ width: "100%", height }}>
      {({ width }) => {
        if (width < 8) return null;
        const innerW = Math.max(1, width - CHART_MARGIN.left - CHART_MARGIN.right);
        const innerH = Math.max(1, height - CHART_MARGIN.top - CHART_MARGIN.bottom);
        const x = (i: number) => xAt(i, count, innerW, 0);
        const onMove = (e: React.PointerEvent<SVGSVGElement>) => {
          const pt = localPoint(e);
          if (!pt) return;
          setHover(indexAtX(pt.x, count, innerW, CHART_MARGIN.left));
        };
        const tipLeft = hover != null ? CHART_MARGIN.left + x(hover) : 0;
        return (
          <div className="relative">
            <svg
              width={width}
              height={height}
              role="img"
              aria-label={ariaLabel}
              className="block overflow-visible"
              onPointerMove={onMove}
              onPointerLeave={() => setHover(null)}
            >
              <Group left={CHART_MARGIN.left} top={CHART_MARGIN.top}>
                {children({ innerW, innerH, x, hover })}
                {hover != null && (
                  <line
                    x1={x(hover)}
                    x2={x(hover)}
                    y1={0}
                    y2={innerH}
                    stroke={CHART_COLORS.crosshair}
                    strokeWidth={1}
                    strokeDasharray="2 2"
                    opacity={0.6}
                    pointerEvents="none"
                  />
                )}
              </Group>
            </svg>
            {hover != null && tooltip && (
              <div
                className="pointer-events-none absolute z-10 -translate-x-1/2 -translate-y-full whitespace-nowrap rounded bg-slate-900/95 px-1.5 py-0.5 text-[10px] tabular-nums text-slate-200 shadow ring-1 ring-slate-700"
                style={{ left: tipLeft, top: 0 }}
              >
                {tooltip(hover)}
              </div>
            )}
          </div>
        );
      }}
    </ParentSize>
  );
}
